import React, {useEffect, useState, useRef} from 'react';
import {View, StyleSheet, Platform} from 'react-native';
import {getTableauWorkbooks} from '@/Hooks/LoginApi/loginApi';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import CCButton from '@/Atoms/CCButton';
import CCColors from '@/Utils/CCColors';
import Spacer from '@/Atoms/Spacer';
import {vs} from '@/Utils/Dimensions';
import Loader from '@/Atoms/CCLoader/CCLoader';
import CCSelect from '@/Atoms/CCSelect';
import SubscriptionModal from '../Subscription/components/Modals';
import {getLocalStorage, setLocalStorage} from '@/Utils/CommonUtils';
import dayjs from 'dayjs';
import {useNonPersistedUserPlanStore} from '@/Store/userPlanStore';

const TableauScriptUrl =
  'https://embedding.tableauusercontent.com/tableau.embedding.3.1.0.min.js';

const TableauReports = ({dashboardData}) => {
  const userData = dashboardData.user;
  const [jwtToken, setJwtToken] = useState<string>('');
  const [currentView, setCurrentView] = useState<string>('');
  const [workbooks, setWorkbooks] = useState<any>([]);
  const [reportLoading, setReportLoading] = useState<boolean>(false);
  const [reportConfig, setReportConfig] = useState<any>();
  const [selectedWorkbook, setSelectedWorkbook] = useState<any>();
  const scriptRef = useRef(null);

  const [limitType, setLimitType] = useState('');
  const [usageLimit, setUsageLimit] = useState(0);
  const [planName, setPlanName] = useState('');
  const {subscriptionModalOpen, setSubscriptionModalOpen} =
    useNonPersistedUserPlanStore();

  const workbookSuccess = result => {
    setLimitType(result?.limit_type);
    setUsageLimit(result?.usage_limit);
    setPlanName(result?.plan_name);
    if (result?.is_permitted === false) {
      setSubscriptionModalOpen(true);
      return;
    }
    setJwtToken(result.token || undefined);
    setLocalStorage(
      'tableau_jwt_expiry',
      dayjs().add(10, 'minute').toISOString(),
    );
    setWorkbooks(Object.values(result.data));
    if (currentView === '') {
      const item = Object.values(result.data)[0];
      if (item) {
        setSelectedWorkbook(item);
        setCurrentView(item?.views[0]?.name);
        console.log('item?.views[0]?.name', item?.views[0]?.name);
      }
    }
  };

  const {mutate: getWorkbooks} = getTableauWorkbooks(workbookSuccess);

  useEffect(() => {
    //setReportLoading(true);
    const loadScript = async () => {
      if (!scriptRef.current) {
        const script = document.createElement('script');
        script.src = TableauScriptUrl;
        script.async = true;
        script.type = 'module';
        scriptRef.current = script;
        document.body.appendChild(script);
      }
    };
    getWorkbooks();
    loadScript();
    // Cleanup function to remove the script on component unmount
    return () => {
      if (scriptRef.current) {
        document.body.removeChild(scriptRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if ((jwtToken || jwtToken === undefined) && selectedWorkbook) {
      try {
        //setReportLoading(true);
        const viz = document.createElement('tableau-viz');
        const url = `https://prod-apnortheast-a.online.tableau.com/t/circlechess/views/${selectedWorkbook.name}/${selectedWorkbook?.views[0]?.name}`;
        viz.src = url;
        viz.toolbar = 'hidden';
        viz.hideTabs = true;
        viz.token = jwtToken;
        viz.addEventListener('firstinteractive', handleFirstInteractive);
        document.getElementById('tableauViz').appendChild(viz);
        //setReportLoading(false);
      } catch (error) {
        console.log('Tableau View error', error);
        //setReportLoading(false);
      }
    }
  }, [jwtToken]);

  const handleFirstInteractive = async event => {
    const viz = document.querySelector('tableau-viz');
    try {
      const workbook = viz.workbook;
      const activeSheet = workbook.activeSheet;
      if (activeSheet.sheetType === 'worksheet') {
        const filters = await activeSheet.getFiltersAsync();
        const mobileFilter = filters.find(f => f.fieldName === 'Mobile Number');
        const userIdFilter = filters.find(f => f.fieldName === 'Userid');
        if (mobileFilter && mobileFilter.filterType === 'categorical') {
          await activeSheet.applyFilterAsync(
            'Mobile Number',
            [userData.mobile_number],
            'replace',
          );
        }
        if (userIdFilter && userIdFilter.filterType === 'range') {
          await activeSheet.applyRangeFilterAsync(
            'Userid',
            {
              min: userData.user_id,
              max: userData.user_id,
            },
            'replace',
          );
        }
      }
      //setReportLoading(false);
    } catch (error) {
      console.log('Tableau Filter error', error);
      //setReportLoading(false);
    }
  };

  const changeView = async (workbook, view) => {
    const viz = document.querySelector('tableau-viz');
    setCurrentView(view);
    const jwt_expiry = await getLocalStorage('tableau_jwt_expiry');
    if (jwt_expiry && dayjs().diff(dayjs(jwt_expiry)) > 0) {
      viz?.remove();
      getWorkbooks();
    }
    //setReportLoading(true);
    // Change the URL of the existing viz
    viz.src = `https://prod-apnortheast-a.online.tableau.com/t/circlechess/views/${workbook.replace(
      /\s/g,
      '',
    )}/${view.replace(/\s/g, '')}`;
  };
  const planMessage = `${limitType} limit of ${usageLimit} ${
    usageLimit === 1 ? 'report' : 'reports'
  } is exhausted. Upgrade now to continue with detailed report.`;
  return (
    <View style={styles.container}>
      <Loader loading={reportLoading} />
      <Box flexDirection="row" flex={1} overflow="hidden">
        <View style={{padding: 20}}>
          <CCSelect
            label="Select Workbook"
            selectedValue={selectedWorkbook?.id}
            onValueChange={id => {
              const workbook = workbooks.find(w => w.id === id);
              const view = workbook.views[0];
              setSelectedWorkbook(workbook);
              changeView(workbook.name, view.name);
            }}
            items={workbooks?.map(workbook => ({
              label: workbook.name,
              value: workbook.id,
            }))}
          />
          <Spacer spacing={vs(24)} />
          <View>
            {selectedWorkbook?.views?.map(view => (
              <CCButton
                style={{
                  marginBottom: 10,
                  backgroundColor:
                    currentView === view.name
                      ? CCColors.Primary.Default
                      : CCColors.Primary.lightBrown,
                }}
                titleStyle={{color: CCColors.TextColor.Dark}}
                onPress={() => changeView(selectedWorkbook.name, view.name)}>
                {view.name}
              </CCButton>
            ))}
          </View>
        </View>
        <View style={styles.webview} id="tableauViz" />
      </Box>
      <SubscriptionModal
        visible={subscriptionModalOpen}
        onClose={() => setSubscriptionModalOpen(false)}
        user={dashboardData?.user}
        planMessage={planMessage}
        currentPlan={planName}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  webview: {
    flex: 1,
  },
});
export default TableauReports;
